import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BasePageLayout,
  BaseBox,
  BaseTypography,
  BaseButton,
} from "../../Common/UI/exports";
import { getStatus } from "../../APIs/operationAPI";
import { openRelease } from "../../APIs/gitFlowReleaseManagementAPI";

function State() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = localStorage.getItem("email");

  const repositories = useMemo(
    () => location.state?.repositories || [],
    [location.state?.repositories]
  );
  const key = location.state?.key;
  const priorityGroupsCount = location.state?.priorityGroupsCount || 0;
  const onlyShowClose = location.state?.onlyShowClose || false;

  const [statuses, setStatuses] = useState({});
  const [notes, setNotes] = useState({});
  const [pullRequests, setPullRequests] = useState({});
  const [currentGroup, setCurrentGroup] = useState("1");

  const fetchStatuses = useCallback(
    async (priorityGroup) => {
      try {
        if (!key) return;

        const statusResponse = await getStatus(key);

        const statusMap = {};
        const pullRequestMap = {};
        const notesMap = {};

        statusResponse.repositories.forEach((repo) => {
          statusMap[repo.repositoryName] = repo.status;
          pullRequestMap[repo.repositoryName] = repo.pullRequestLink
            ? repo.pullRequestLink.split(",").map((link) => link.trim())
            : [];
          notesMap[repo.repositoryName] = repo.notes;
        });

        setStatuses((prev) => ({ ...prev, ...statusMap }));
        setPullRequests((prev) => ({ ...prev, ...pullRequestMap }));
        setNotes((prev) => ({ ...prev, ...notesMap }));

        const groupRepos = repositories.filter(
          (repo) => repo.priorityGroup === priorityGroup
        );
        const groupStatus = groupRepos.every((repo) =>
          statusMap[repo.name]?.toLowerCase().includes("opened")
        );

        if (groupStatus) {
          const nextGroup = (parseInt(priorityGroup, 10) + 1).toString();
          if (parseInt(priorityGroup, 10) < priorityGroupsCount) {
            await openRelease(key, nextGroup);
            setCurrentGroup(nextGroup);
          }
        }
      } catch (error) {
        console.error("Error fetching statuses:", error);
      }
    },
    [key, priorityGroupsCount, repositories]
  );

  useEffect(() => {
    if (key) fetchStatuses(currentGroup);
  }, [fetchStatuses, currentGroup, key]);

  useEffect(() => {
    const hasOpenedRelease = localStorage.getItem("hasOpenedRelease");
    if (!hasOpenedRelease && key) {
      openRelease(key, "1")
        .then(async () => {
          localStorage.setItem("hasOpenedRelease", "true");
          await fetchStatuses("1");
        })
        .catch(console.error);
    }
  }, [key, fetchStatuses]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchStatuses(currentGroup);
  }, [fetchStatuses, currentGroup]);

  useEffect(() => {
    const startTime = Date.now();
    const intervalId = setInterval(() => {
      handleRefresh();
      if (Date.now() - startTime >= 3600000) clearInterval(intervalId);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [handleRefresh]);

  const groupedByPriority = useMemo(
    () =>
      repositories.reduce((groups, repo) => {
        const { priorityGroup } = repo;
        if (!groups[priorityGroup]) groups[priorityGroup] = [];
        groups[priorityGroup].push(repo);
        return groups;
      }, {}),
    [repositories]
  );

  return (
    <BasePageLayout sidebarItems={[]}>
      <BaseBox
        sx={{
          height: "calc(100vh - 140px)",
          display: "flex",
          flexDirection: "column",
          maxWidth: "1160px",
          mx: "auto",
          mt: 0,
          px: 2,
        }}
      >
        <BaseTypography sx={{ fontSize: "1.2rem", mb: 2, fontWeight: 600 }}>
          Release / Hotfix Status
        </BaseTypography>

        {/* Request ID */}
        <BaseBox sx={{ mb: 2 }}>
          <BaseTypography>
            <strong>Request ID:</strong> {key}
          </BaseTypography>
        </BaseBox>

        {/* Scrollable repositories table */}
        <BaseBox
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            mb: 2,
            pr: 1,
          }}
        >
          {Object.entries(groupedByPriority).length > 0 ? (
            Object.entries(groupedByPriority).map(([priority, repos]) => (
              <BaseBox key={priority} sx={{ mb: 3 }}>
                <BaseTypography sx={{ fontWeight: 600, mb: 1 }}>
                  Priority Group: {priority}
                </BaseTypography>
                <BaseBox sx={{ overflowX: "auto" }}>
                  <table
                    className="repositories-table"
                    style={{ width: "100%" }}
                  >
                    <thead>
                      <tr>
                        <th>Repository Name</th>
                        <th>Status</th>
                        <th>Pull Request</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repos.map((repo) => (
                        <tr key={repo.id}>
                          <td>{repo.name}</td>
                          <td>{statuses[repo.name] || "initializing..."}</td>
                          <td>
                            {pullRequests[repo.name]?.length > 0
                              ? pullRequests[repo.name].map((link, i) => (
                                  <div key={i}>
                                    <a
                                      href={link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {link}
                                    </a>
                                  </div>
                                ))
                              : "N/A"}
                          </td>
                          <td>{notes[repo.name]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </BaseBox>
              </BaseBox>
            ))
          ) : (
            <BaseTypography>No repositories to display.</BaseTypography>
          )}
        </BaseBox>

        {/* Action Buttons */}
      <BaseBox
  sx={{
    border: "1px solid #ccc",
    borderRadius: "8px",
    p: 2,
    mb: 2,
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    backgroundColor: "#f9f9f9",
    justifyContent: "center",
    alignItems: "center",
  }}
>
  {!onlyShowClose && (
    <>
      <BaseButton
        onClick={() => navigate(-2)}
        color="secondary"
        sx={{ width: "auto", minWidth: 100 }}
      >
        Back
      </BaseButton>
      <BaseButton
        onClick={handleRefresh}
        color="primary"
        sx={{ width: "auto", minWidth: 100 }}
      >
        Refresh
      </BaseButton>
      <BaseButton
        onClick={() => navigate("/home")}
        color="secondary"
        sx={{ width: "auto", minWidth: 100 }}
      >
        Cancel
      </BaseButton>
    </>
  )}
  <BaseButton
    onClick={() => navigate("/home")}
    color="secondary"
    sx={{ width: "auto", minWidth: 100 }}
  >
    Close
  </BaseButton>
</BaseBox>

      </BaseBox>
    </BasePageLayout>
  );
}

export default State;
